import React, { useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import GridItem from '../../../../layout/GridComponents/GridItem';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { greys, mainColors } from '../../../../../styling/theme';
import {
  percentageToNdecialPlaces,
  percentageToNdecialPlacesAddZeros,
} from '../../../../../utilities/numberFormatters';
import { DataObject } from '../../../../../types/redux/data/dataTypes';
import { useDispatch } from 'react-redux';
import usePngFromRecharts from '../../../../../hooks/usePngFromRecharts';
import { addComponentToPdfExport } from '../../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../../types/redux/pdfExports/FileExportsStore';
import FileSaver from 'file-saver';
import ExportButton from '../../../../feedback/ExportButton';
import ChartDownloadButton from '../../../../buttons/ChartDownloadButton';

interface StockSelectionPerformanceChartProps {
  data: DataObject;
}

interface StockSelectionPerformanceChartData {
  sector: string;
  fund_sector_returns: number;
  benchmark_sector_returns: number;
  difference_fund_sector_returns_vs_fwbr: number;
}

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0.62rem 1.56rem 0.62rem 0.94rem',
  },
  cardTitle: {
    color: mainColors.mainBlue,
    fontSize: '1.38rem',
    fontWeight: 400,
    margin: '0.62rem 0.94rem',
  },
  customTooltipContainer: {
    backgroundColor: 'white',
    padding: '0.62rem 0.94rem',
    borderRadius: '0.31rem',
    border: `1px solid ${mainColors.mainBlue}`,
    width: '31.25rem',
    color: mainColors.mainBlue,
  },
  tooltipName: {
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  tooltipItem: {
    fontSize: '1.00rem',
    fontWeight: 600,
    color: mainColors.mainBlue,
    display: 'flex',
    justifyContent: 'space-between',
  },
  exportButtons: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const CustomTooltip = ({ active, payload, label }: any) => {
  const classes = useStyles();
  if (active && payload && payload.length) {
    return (
      <div className={classes.customTooltipContainer}>
        <div>
          <div className={classes.tooltipName}>{label}</div>
          <hr />
          <div className={classes.tooltipItem} style={{ color: '#AA3FCC' }}>
            <div>Fund Sector Returns:</div>
            <div>
              {percentageToNdecialPlacesAddZeros(
                payload.find((item: any) => item.name === 'fund_sector_returns')
                  .value / 100,
                2,
              )}
            </div>
          </div>
          <div className={classes.tooltipItem} style={{ color: '#EA812F' }}>
            <div>Fund Weighted Benchmark Returns (FWBR):</div>
            <div>
              {percentageToNdecialPlacesAddZeros(
                payload.find(
                  (item: any) =>
                    item.name === 'fund_weighted_benchmark_returns',
                ).value / 100,
                2,
              )}
            </div>
          </div>
          <div className={classes.tooltipItem} style={{ color: '#00ABD9' }}>
            <div>Difference:</div>
            <div>
              {percentageToNdecialPlacesAddZeros(
                payload.find(
                  (item: any) =>
                    item.name === 'difference_fund_sector_returns_vs_fwbr',
                ).value / 100,
                2,
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};

const buildChartData = (
  inputData: any,
): StockSelectionPerformanceChartData[] => {
  if (
    !inputData.data ||
    !inputData.data.length ||
    !inputData.data[0].stock_selection
  ) {
    return [];
  } else {
    return inputData.data[0].stock_selection;
  }
};

const StockSelectionPerformanceChart: React.FC<
  StockSelectionPerformanceChartProps
> = ({ data }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const id = `stock-selection-performance`;
  const title = `Stock Selection Performance`;

  const builtChartData = buildChartData(data);

  const { ref, handleDownload } = usePngFromRecharts();

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: id,
        handler: handleDownload,
        type: PdfComponentType.LINE_CHART,
        title: title,
      }),
    );
  }, [ref]);

  const saveImage = async () => {
    const imageData = await handleDownload();
    if (imageData) {
      FileSaver.saveAs(imageData.data, `${id}.png`);
    }
  };

  return (
    <GridItem xs={12} card>
      <div className={classes.toolbar}>
        <h2 className={classes.cardTitle}>{title}</h2>
        <div className={classes.exportButtons}>
          <ExportButton
            fileName={`${id}.csv`}
            exportData={builtChartData}
            fields={Object.keys(builtChartData[0])}
            fieldsMap={[
              { key: 'sector_name', label: 'Sector' },
              { key: 'fund_sector_returns', label: 'Fund Sector Returns' },
              {
                key: 'fund_weighted_benchmark_returns',
                label: 'Fund Weighted Benchmark Returns (FWBR)',
              },
              {
                key: 'difference_fund_sector_returns_vs_fwbr',
                label: 'Difference',
              },
            ]}
          />
          <ChartDownloadButton handler={saveImage} />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          width={500}
          height={400}
          barCategoryGap={'20%'}
          data={builtChartData}
          margin={{
            top: 20,
            right: 50,
            left: 30,
            bottom: 20,
          }}
          ref={ref}
        >
          <CartesianGrid strokeDasharray="5 5" />
          <XAxis
            dataKey="sector_name"
            interval={0}
            angle={-25}
            textAnchor="end"
            height={100}
            tick={{
              stroke: mainColors.mainBlue,
              strokeWidth: 1,
              fontSize: '0.81rem',
            }}
            tickLine={true}
          >
            <Label
              style={{
                textAnchor: 'middle',
                fontSize: '125%',
                fontWeight: 400,
                color: mainColors.mainBlue,
              }}
              value={'Sector'}
              position={'insideBottom'}
            />
          </XAxis>
          <YAxis
            width={70}
            tickFormatter={(tickItem) => {
              return percentageToNdecialPlaces(tickItem / 100, 1);
            }}
            tick={{
              stroke: mainColors.mainBlue,
              strokeWidth: 1,
              fontSize: '0.81rem',
            }}
            tickLine={true}
          >
            <Label
              style={{
                textAnchor: 'middle',
                fontSize: '125%',
                fontWeight: 400,
                color: mainColors.mainBlue,
              }}
              angle={270}
              value={'Returns %'}
              position={'insideLeft'}
            />
          </YAxis>
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke={greys.grey600} />
          <Bar dataKey={'fund_sector_returns'} fill={'#AA3FCC'} />
          <Bar dataKey={'fund_weighted_benchmark_returns'} fill={'#EA812F'} />
          <Bar
            dataKey={'difference_fund_sector_returns_vs_fwbr'}
            fill={'#00ABD9'}
          />
        </BarChart>
      </ResponsiveContainer>
    </GridItem>
  );
};

export default StockSelectionPerformanceChart;
